import { createClient } from '@supabase/supabase-js'

const supabaseUrl = process.env.REACT_APP_SUPABASE_URL
const supabaseKey = process.env.REACT_APP_SUPABASE_ANON_KEY

export const supabase = createClient(supabaseUrl, supabaseKey)

// Map email → display name & role
// Maps the Supabase Auth login email -> display name + role.
// Emails must match exactly what is created in Supabase Authentication.
// role 'superadmin' = global access (Devi); 'agent' = own records only.
export const USER_PROFILES = {
  'devi.alvioliana@icscompute.com':   { name: 'Devi',    role: 'superadmin' },
  'halimah.kasanah@icscompute.com':   { name: 'Halimah', role: 'agent' },
  'yonila.amalia@icscompute.com':     { name: 'Yonila',  role: 'agent' },
  'salsabila.aidina@icscompute.com':  { name: 'Salsa',   role: 'agent' },
  'yulisa.baisela@icscompute.com':    { name: 'Yulisa',  role: 'agent' },
}

export const auth = {
  async signIn(email, password) {
    const { data, error } = await supabase.auth.signInWithPassword({ email, password })
    if (error) throw error
    return data
  },
  async signOut() {
    const { error } = await supabase.auth.signOut()
    if (error) throw error
  },
  async getSession() {
    const { data } = await supabase.auth.getSession()
    return data.session
  },
  onAuthStateChange(callback) {
    return supabase.auth.onAuthStateChange((_event, session) => callback(session))
  },
}

export const db = {
  // Call Logs
  async getCallLogs() {
    const { data, error } = await supabase.from('call_logs').select('*').order('created_at', { ascending: false })
    if (error) throw error
    return data
  },
  async addCallLog(log) {
    const { data, error } = await supabase.from('call_logs').insert([log]).select()
    if (error) throw error
    return data[0]
  },
  async updateCallLog(id, updates) {
    const { data, error } = await supabase.from('call_logs').update(updates).eq('id', id).select()
    if (error) throw error
    return data[0]
  },
  async deleteCallLog(id) {
    const { error } = await supabase.from('call_logs').delete().eq('id', id)
    if (error) throw error
  },

  // Pipeline
  async getPipeline() {
    const { data, error } = await supabase.from('pipeline').select('*').order('created_at', { ascending: false })
    if (error) throw error
    return data
  },
  async addDeal(deal) {
    const { data, error } = await supabase.from('pipeline').insert([deal]).select()
    if (error) throw error
    return data[0]
  },
  async updateDealStage(id, stage) {
    const { error } = await supabase.from('pipeline').update({ stage }).eq('id', id)
    if (error) throw error
  },
  async updateDeal(id, updates) {
    const { error } = await supabase.from('pipeline').update(updates).eq('id', id)
    if (error) throw error
  },
  async deleteDeal(id) {
    const { error } = await supabase.from('pipeline').delete().eq('id', id)
    if (error) throw error
  },

  // Reminders
  async getReminders() {
    const { data, error } = await supabase.from('reminders').select('*').order('follow_up_date', { ascending: true })
    if (error) throw error
    return data
  },
  async addReminder(reminder) {
    const { data, error } = await supabase.from('reminders').insert([reminder]).select()
    if (error) throw error
    return data[0]
  },
  async markReminderDone(id) {
    const { error } = await supabase.from('reminders').update({ done: true }).eq('id', id)
    if (error) throw error
  },
  async deleteReminder(id) {
    const { error } = await supabase.from('reminders').delete().eq('id', id)
    if (error) throw error
  },

  // DB Contacts (user-added)
  async getDbContacts() {
    const { data, error } = await supabase.from('db_contacts').select('*').order('created_at', { ascending: false })
    if (error) throw error
    return data
  },
  async addDbContact(contact) {
    const { data, error } = await supabase.from('db_contacts').insert([contact]).select()
    if (error) throw error
    return data[0]
  },
  async addDbContactsBulk(contacts) {
    const { data, error } = await supabase.from('db_contacts').insert(contacts).select()
    if (error) throw error
    return data
  },
  async updateDbContact(id, updates) {
    const { error } = await supabase.from('db_contacts').update(updates).eq('id', id)
    if (error) throw error
  },
  async deleteDbContact(id) {
    const { error } = await supabase.from('db_contacts').delete().eq('id', id)
    if (error) throw error
  },

  // ── Tasks (follow-up engine) ───────────────────────────────────────────
  // Resilient: if the `tasks` table doesn't exist yet (migration not run),
  // reads return [] instead of crashing the app.
  async getTasks() {
    const { data, error } = await supabase.from('tasks').select('*').order('due_at', { ascending: true })
    if (error) {
      if (isMissingTable(error)) return []
      throw error
    }
    return data
  },
  async addTask(task) {
    const { data, error } = await supabase.from('tasks').insert([task]).select()
    if (error) throw error
    return data[0]
  },
  // Insert that tolerates the dedupe unique index: if an equivalent open task
  // already exists, treat as success (idempotent) instead of throwing.
  async addTaskIdempotent(task) {
    const { data, error } = await supabase.from('tasks').insert([task]).select()
    if (error) {
      if (isUniqueViolation(error)) return null // duplicate open task already exists
      throw error
    }
    return data[0]
  },
  async updateTask(id, updates) {
    const { data, error } = await supabase.from('tasks')
      .update({ ...updates, updated_at: new Date().toISOString() }).eq('id', id).select()
    if (error) throw error
    return data[0]
  },
  async deleteTask(id) {
    const { error } = await supabase.from('tasks').delete().eq('id', id)
    if (error) throw error
  },

  // ── Activities (append-only history / audit) ─────────────────────────────
  async getActivities(limit = 1000) {
    const { data, error } = await supabase.from('activities').select('*')
      .order('activity_at', { ascending: false }).limit(limit)
    if (error) {
      if (isMissingTable(error)) return []
      throw error
    }
    return data
  },
  async addActivity(activity) {
    const { data, error } = await supabase.from('activities').insert([activity]).select()
    if (error) throw error
    return data[0]
  },

  // ── Profiles (uid -> agent name + role), used by RLS once enabled ────────
  async upsertProfile(userId, agentName, role) {
    const { error } = await supabase.from('profiles')
      .upsert({ user_id: userId, agent_name: agentName, role }, { onConflict: 'user_id' })
    if (error && !isMissingTable(error)) throw error
  },

  // ── Diary / Catatan Harian (personal notes per agent per date) ───────────
  async getDiary() {
    const { data, error } = await supabase.from('diary_entries').select('*').order('entry_date', { ascending: false })
    if (error) { if (isMissingTable(error)) return []; throw error }
    return data
  },
  async addDiary(entry) {
    const { data, error } = await supabase.from('diary_entries').insert([entry]).select()
    if (error) throw error
    return data[0]
  },
  async updateDiary(id, updates) {
    const { data, error } = await supabase.from('diary_entries')
      .update({ ...updates, updated_at: new Date().toISOString() }).eq('id', id).select()
    if (error) throw error
    return data[0]
  },
  async deleteDiary(id) {
    const { error } = await supabase.from('diary_entries').delete().eq('id', id)
    if (error) throw error
  },

  // ── Personal To-Do (distinct from lead follow-up tasks) ──────────────────
  async getTodos() {
    const { data, error } = await supabase.from('todos').select('*').order('task_date', { ascending: false })
    if (error) { if (isMissingTable(error)) return []; throw error }
    return data
  },
  async addTodo(todo) {
    const { data, error } = await supabase.from('todos').insert([todo]).select()
    if (error) throw error
    return data[0]
  },
  async updateTodo(id, updates) {
    const { data, error } = await supabase.from('todos')
      .update({ ...updates, updated_at: new Date().toISOString() }).eq('id', id).select()
    if (error) throw error
    return data[0]
  },
  async deleteTodo(id) {
    const { error } = await supabase.from('todos').delete().eq('id', id)
    if (error) throw error
  },

  // ── Ownership transfers (superadmin reassigns a db_contact to another agent) ─
  async addOwnershipTransfer(row) {
    const { data, error } = await supabase.from('ownership_transfers').insert([row]).select()
    if (error) { if (isMissingTable(error)) return null; throw error }
    return data[0]
  },

  // ── Cross-agent duplicate lookup (SECURITY DEFINER RPC, bypasses RLS) ──────
  // Returns { exists, owner_agent, created_by_agent, company, match_on } or null
  // when the RPC isn't deployed yet (caller then falls back to client check).
  async findContactDuplicate({ company, domain }) {
    const { data, error } = await supabase.rpc('find_contact_duplicate', {
      p_company: company || '', p_domain: domain || '',
    })
    if (error) {
      // function missing (not yet migrated) or not callable → signal fallback
      const msg = (error.message || '').toLowerCase()
      if (msg.includes('find_contact_duplicate') || msg.includes('function') || isMissingTable(error)) return null
      throw error
    }
    const row = Array.isArray(data) ? data[0] : data
    if (!row) return { exists: false }
    return { exists: true, owner_agent: row.owner_agent, created_by_agent: row.created_by_agent, company: row.company, match_on: row.match_on }
  },
}

// Supabase/PostgREST error for a table that doesn't exist yet.
function isMissingTable(error) {
  const code = error?.code || ''
  const msg = (error?.message || '').toLowerCase()
  return code === '42P01' || code === 'PGRST205' || msg.includes('does not exist') || msg.includes('could not find the table')
}
// Unique-constraint violation (dedupe index) = duplicate, safe to ignore.
function isUniqueViolation(error) {
  return (error?.code || '') === '23505'
}
